import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  AdminBorderUprightSpanMapping,
  UpsertBorderUprightSpanMappingBody,
} from "./types/uprightSpanMappings";

export const useUprightSpanMappingsApi = () => {
  const api = useApi();

  const getAdminUprightSpanMapping = useCallback(
    (id: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/upright_span_mappings/${id}`;
      return useSWRMutation<AdminBorderUprightSpanMapping, CustomError, string>(url, (url) =>
        api.get<AdminBorderUprightSpanMapping>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminUprightSpanMappings = useCallback(
    (systemId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/systems/${systemId}/upright_span_mappings`, query);
      return useSWRImmutable<Paginated<AdminBorderUprightSpanMapping>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminBorderUprightSpanMapping>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminUprightSpanMapping = useCallback(
    async (systemId: string, body: UpsertBorderUprightSpanMappingBody): Promise<{ id: string }> => {
      const url = "/admin/settings/border/upright_span_mappings";
      const id = await api.post<{ id: string }, UpsertBorderUprightSpanMappingBody>(url, body);
      mutateCache([
        url,
        `/admin/settings/border/systems/${systemId}/upright_span_mappings`,
        "/admin/settings/border/ballasts",
        "/admin/settings/border/bases",
      ]);
      return id;
    },
    [api],
  );

  const updateAdminUprightSpanMapping = useCallback(
    async (systemId: string, uprightSpanMappingId: string, body: UpsertBorderUprightSpanMappingBody): Promise<void> => {
      const url = `/admin/settings/border/upright_span_mappings/${uprightSpanMappingId}`;
      await api.put<UpsertBorderUprightSpanMappingBody>(url, body);
      mutateCache([
        "/admin/settings/border/upright_span_mappings",
        `/admin/settings/border/systems/${systemId}/upright_span_mappings`,
        "/admin/settings/border/ballasts",
        "/admin/settings/border/bases",
      ]);
    },
    [api],
  );

  const deleteAdminUprightSpanMapping = useCallback(
    async (systemId: string, uprightSpanMappingId: string): Promise<void> => {
      const url = `/admin/settings/border/upright_span_mappings/${uprightSpanMappingId}`;
      await api.del(url);
      mutateCache([
        "/admin/settings/border/upright_span_mappings",
        `/admin/settings/border/systems/${systemId}/upright_span_mappings`,
        "/admin/settings/border/ballasts",
        "/admin/settings/border/bases",
      ]);
    },
    [api],
  );

  return {
    getAdminUprightSpanMapping,
    useAdminUprightSpanMappings,
    insertAdminUprightSpanMapping,
    updateAdminUprightSpanMapping,
    deleteAdminUprightSpanMapping,
  };
};
